import {
    AutoTocEntry,
    CodeArticle,
    PublicationSheet,
    PublicationSheetWithId,
    Tree,
} from '@livv/models';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { FC, PropsWithChildren, useEffect } from 'react';
import AutoTocPage from './AutoTocPage';
import BookContainer from './components/BookContainer';
import FirstPage from './FirstPage';
import Nodes from './Nodes';
import DocumentNotFound from '@components/DocumentNotFound';
import { AuthEvent } from '@utils/consts/events';
import BookContextProvider, { useBookPageContext } from '@utils/context/book';
import { useBookPathSlugs } from '@utils/hooks/useBookSlugs';
import { DecisionsCitationsInPublicationSheet } from '@utils/serverSide/book/getDecisionsCitationsInPublicationSheet';
import { PreviewsContent } from '@utils/serverSide/book/getPreviewsContent';

export interface BookPageProps extends PropsWithChildren {
    autoToc?: AutoTocEntry[];
    book: Tree;
    bookId: string;
    bookPath?: string;
    decisionsCitationsInPublicationSheet?: DecisionsCitationsInPublicationSheet[];
    deletedCodeArticle?: boolean;
    isCodeArticlePage: boolean;
    previewsContent?: PreviewsContent;
    sheet?: CodeArticle | PublicationSheet;
    sheets?: PublicationSheetWithId[];
}

export const BookContent: FC = () => {
    const router = useRouter();
    const { bookPath } = useBookPathSlugs();
    const { bookMeta } = useBookPageContext();
    let pageContent = <FirstPage />;

    // Handle logout event to refresh the page (SSR)
    useEffect(() => {
        const handleLogout = (eventName: AuthEvent) => {
            if (eventName === AuthEvent.LOGOUT) {
                router.reload();
            }
        };

        router.events.on('routeChangeComplete', handleLogout);

        return () => {
            router.events.off('routeChangeComplete', handleLogout);
        };
    }, [router]);

    if (bookPath && bookPath.length) {
        pageContent = <Nodes />;
    } else if (bookMeta.hasAutoToc) {
        pageContent = <AutoTocPage />;
    }

    return <BookContainer>{pageContent}</BookContainer>;
};

const BookWrapper: NextPage<BookPageProps> = ({ deletedCodeArticle, ...props }) => {
    if (deletedCodeArticle) {
        return <DocumentNotFound />;
    }

    return (
        <BookContextProvider {...props}>
            <BookContent />
        </BookContextProvider>
    );
};

export default BookWrapper;
